import { sql, type Kysely, type SelectQueryBuilder } from 'kysely';

import type { BatchStatement } from '../db/batch.js';
import type { TaprootDb } from '../db/client.js';
import type {
  ContentItemRow,
  ContentStatus,
  ContentTypeKind,
  ContentTypeRow,
  Database,
  FieldRow,
  RevisionReason,
} from '../db/schema.js';
import { now, parseJson, stringifyJson } from '../db/values.js';
import { newId } from '../ids.js';
import { validateItemData } from '../validation/fields.js';
import type { ItemSort } from './itemSort.js';
import { rankedSearchIds, toMatchQuery } from './search.js';
import {
  buildRevisionStatement,
  getRevision,
  revisionSequence,
  snapshotIsUnchanged,
  RevisionError,
} from './revisions.js';
import { planAssignmentIndex } from './taxonomies.js';
import {
  planDerivedIndexes,
  planTextIndexDelete,
  type IndexedValueKind,
} from './derivedIndex.js';
import { blockTypeRegistry, typeKeepsRevisions, type ContentTypeWithFields } from './types.js';
import {
  buildCollectionPath,
  buildPath,
  computeSubtreeRewrite,
  descendantPathRange,
  normalizePath,
  slugify,
  wouldCreateCycle,
  type PathRewrite,
  type SubtreeNode,
} from './paths.js';

export class ContentItemError extends Error {
  override name = 'ContentItemError';
  constructor(
    message: string,
    readonly code:
      | 'not_found'
      | 'validation_failed'
      | 'cycle'
      | 'singleton_exists'
      | 'invalid_parent'
      | 'stale_order' = 'validation_failed',
    readonly fieldErrors: Record<string, string[]> = {},
  ) {
    super(message);
  }
}

export interface ContentItem extends Omit<ContentItemRow, 'data' | 'seo'> {
  data: Record<string, unknown>;
  seo: SeoData;
}

/**
 * Per-item SEO overrides.
 *
 * Every key is optional and absence means "fall back", which is what `resolveSeo` implements. No
 * length limits live here on purpose — see SEO_GUIDANCE in seo.ts for why an over-length title is
 * a warning in the editor rather than a rejected save.
 */
export interface SeoData {
  metaTitle?: string;
  metaDescription?: string;
  ogImageId?: string;
  noIndex?: boolean;
}

export function hydrateItem(row: ContentItemRow): ContentItem {
  return {
    ...row,
    data: parseJson<Record<string, unknown>>(row.data, {}),
    seo: parseJson<SeoData>(row.seo, {}),
  };
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * A named order, plus the one that is not part of the vocabulary.
 *
 * `relevance` is deliberately **not** in `ITEM_SORTS`. That set is the query field's sort menu — it
 * is validated into a saved query, rendered in the builder, and emitted into the generated types —
 * and "most relevant" means nothing to a listing with no search term. Offering it there would put a
 * choice in front of an editor that answers their question by ignoring them. It is reached by
 * searching instead: a list with a term and no explicitly named order comes back ranked.
 */
type ListOrder = ItemSort | 'relevance';

export interface ListItemsOptions extends ItemFilters {
  limit?: number;
  offset?: number;
  /**
   * Defaults to `path` — or to relevance when there is a `search` and no named order, which is what
   * makes the admin's cross-type search and the delivery search endpoint rank without either of them
   * asking. Naming an order always wins, so a search page offering "newest first" gets it.
   */
  sort?: ItemSort;
  /**
   * Which of the item's own fields `field_asc` / `field_desc` order by, and how it compares.
   *
   * The kind decides the column, and getting it wrong is silent rather than loud: sorted as text,
   * `10` comes before `9` and a numeric ordering is simply wrong in a way that looks plausible.
   * Callers get it from the field definition — `indexedValueKind` — rather than guessing.
   */
  sortField?: { apiId: string; kind: IndexedValueKind };
}

/** The narrowing part of a list query, without paging. */
export interface ItemFilters {
  contentTypeId?: string;
  status?: ContentStatus;
  parentId?: string | null;
  /**
   * Narrow to everything **below** a path — a whole branch of the site tree.
   *
   * `parentId` is one level and this is the branch, which is the question a section-scoped listing
   * and a search inside one part of a site both actually have.
   *
   * **Descendants only, and implemented as a range rather than a `like`.** See
   * `descendantPathRange`: the index is BINARY, D1 refuses the PRAGMA that would let `like` use it,
   * and the root is excluded so the predicate needs no `or` — which SQLite has already been shown
   * here to refuse to spend indexes across.
   */
  pathPrefix?: string;
  search?: string;
  /**
   * Term ids to filter by — an item carrying any one of them matches.
   *
   * A *list* rather than a single id because a term filter means the whole branch: filing something
   * under "Sciences" should find it when someone filters by "Academics". The expansion is the
   * caller's, through `termIdsForBranch`, so this stays a synchronous query builder that the status
   * counts can share.
   *
   * `undefined` means no filter. An empty array means *nothing matches*, following `listMedia`'s
   * precedent — with `in ()` being a syntax error, the tempting fallthrough is the dangerous one,
   * because it silently turns "filter by a term with no members" into "show everything".
   */
  termIds?: string[];
  /**
   * Narrow to what a visitor may see, through the one shared predicate.
   *
   * Distinct from `status: 'published'`, which is the trap this exists to avoid: "visible" is two
   * conditions, because a `scheduled` item whose moment has passed is live whether or not a sweep
   * has run. The delivery API needs exactly this, and needed it in SQL rather than as a filter over
   * the results — otherwise `total` counts rows the caller then discards, and paging is wrong by
   * however many drafts happened to fall in the page.
   */
  visibleOnly?: boolean;
  /**
   * Narrow to items whose content type is one of these kinds.
   *
   * The delivery listing is the caller: a singleton's `path` is the synthetic
   * `/__singleton/{api_id}`, which is not a URL anybody can link to, so offering one to a consumer
   * building an index hands them a broken link. Expressed as kinds rather than "exclude singletons"
   * because `page` and `collection` are exactly the kinds that *have* public URLs — which is the
   * property the caller actually wants, and the one `kindHasPublicPath` names.
   */
  contentTypeKinds?: ContentTypeKind[];
  /**
   * Narrow to types whose items are served at their own URLs.
   *
   * For every caller that is about to hand somebody a **link**: site search, and an index page that
   * did not name a type. A collection with item pages turned off — a staff directory — is real
   * content that appears on a page the site builds, and listing it among things a visitor can click
   * through to offers a URL that answers 404.
   *
   * Not applied when a caller names the type it wants. Asking for `type=person` is asking for
   * people, and answering nothing because they have no pages of their own would be refusing the
   * question rather than answering it — that listing is exactly how a directory is built.
   */
  contentTypeHasItemPages?: boolean;
  /**
   * Narrow by an item's own field values, through the derived value index.
   *
   * The reason the index exists: "events whose `starts_at` is after now" has no other SQL path,
   * because `data` is TEXT. Several filters are ANDed — each is its own `EXISTS`, so an item has to
   * satisfy all of them, and a multi-value field satisfying one counts once rather than
   * multiplying the row.
   */
  valueFilters?: ItemValueFilter[];
}

/**
 * One condition on an indexed field value.
 *
 * Dates only, so far, and stated as a bound rather than as "upcoming": *when* now is belongs to the
 * caller, not to stored data. A `dateFilter: 'upcoming'` saved on a page would otherwise be a
 * timestamp frozen at whatever moment somebody last pressed save — the same booby trap a stale
 * `publish_at` is.
 */
export interface ItemValueFilter {
  /** The field's `api_id`. */
  field: string;
  operator: 'after' | 'before';
  /** ISO 8601, compared against `value_date`. */
  value: string;
}

/**
 * Whether items of this kind have a URL a visitor can request.
 *
 * `page` and `collection` do. A `singleton` gets the synthetic `/__singleton/{api_id}`, which is an
 * addressing convenience rather than a route, and a `block` type has no items at all.
 *
 * Asked as a question about the kind rather than as `kind !== 'singleton'` written out at each call
 * site — the preview link and the split-view pane both gate on it, and two copies is how they end
 * up disagreeing about the same page.
 */
export function kindHasPublicPath(kind: ContentTypeKind): boolean {
  return kind === 'page' || kind === 'collection';
}

/**
 * Whether this type's items are served at their own URLs.
 *
 * `kindHasPublicPath` asks about the *kind* and cannot answer this: a collection is addressed by
 * path whether or not the site publishes one, and a staff directory is the case where it should not.
 * The people are content items in every other respect — created, versioned, classified, listed on a
 * page the site builds — and giving each a URL means a consumer's catch-all renders a bare field
 * dump at `/people/anybody`, the admin offers a link to a page nobody designed, and site search
 * returns it.
 *
 * Asked as a question about the type rather than read off the column at each call site, because the
 * column is only meaningful for a collection: a page is a node in the site tree and a singleton has
 * no item URL to begin with. A caller reading `item_pages` directly is one that will forget that.
 */
export function typeHasItemPages(
  contentType: Pick<ContentTypeRow, 'kind' | 'item_pages'>,
): boolean {
  if (!kindHasPublicPath(contentType.kind)) return false;
  return contentType.kind !== 'collection' || contentType.item_pages === 1;
}

/**
 * The address on the public site where this item is rendered, or null if there is none.
 *
 * This is the question the preview pane, the mint endpoints, and the editor's path link all
 * actually have — `kindHasPublicPath` was standing in for it, and answered "no" for every
 * singleton because a singleton's own `path` cannot say where it is shown. That was right about
 * `/__singleton/{api_id}` and wrong about singletons: a homepage assembled from blocks is rendered
 * at `/`, and `content_types.preview_path` is how a site says so.
 *
 * Null is returned for a singleton nobody has configured, and that stays the default deliberately.
 * A settings record holding an address and social links has no page, and a preview that framed the
 * site's front page while claiming to show that record is worse than no preview at all — the same
 * failure `resolveSeo` living in core exists to prevent, one level up.
 *
 * A `page` or `collection` answers with `item.path`, ignoring the column entirely: those items
 * already know where they live, and reading a second source for it is how the two drift.
 *
 * A collection with **no item pages** answers null, because there is nothing to open — the site
 * serves no URL for it and `resolveDelivery` answers `not_found` at its path. Deliberately *not*
 * redirected to whatever listing shows the item instead: that page is a site route Taproot does not
 * know, and framing one while claiming to preview this item is the failure the singleton branch
 * above already refuses.
 *
 * **This is not a delivery route.** The consumer still asks `resolve` for `item.path`, which is
 * what a preview token is a capability over. This only says which URL to open.
 */
export function previewPathFor(
  contentType: Pick<ContentTypeRow, 'kind' | 'preview_path' | 'item_pages'>,
  item: Pick<ContentItem, 'path'>,
): string | null {
  if (kindHasPublicPath(contentType.kind)) {
    return typeHasItemPages(contentType) ? item.path : null;
  }
  if (contentType.kind === 'singleton') return contentType.preview_path || null;
  return null;
}

type ItemQuery = SelectQueryBuilder<Database, 'content_items', {}>;

/**
 * The WHERE clauses shared by the item list and its status counts.
 *
 * Extracted rather than duplicated because the counts label the list: a facet that applied a
 * different search than the rows beneath it would be worse than showing no count at all. Typed
 * against the pre-`select` builder, which is where both callers apply it.
 */
function applyItemFilters(query: ItemQuery, filters: ItemFilters): ItemQuery {
  let q = query;

  if (filters.contentTypeId) q = q.where('content_type_id', '=', filters.contentTypeId);
  if (filters.status) q = q.where('status', '=', filters.status);
  if (filters.visibleOnly) q = q.where(visibleToPublic);

  const kinds = filters.contentTypeKinds;
  const needsKinds = kinds !== undefined && kinds.length > 0;

  if (needsKinds || filters.contentTypeHasItemPages) {
    // EXISTS rather than a join, so an item is counted once and the shape of the query the status
    // facets share is unchanged. One subquery however many conditions it carries.
    q = q.where((eb) =>
      eb.exists(
        eb
          .selectFrom('content_types')
          .select('content_types.id')
          .whereRef('content_types.id', '=', 'content_items.content_type_id')
          .$if(needsKinds, (sub) => sub.where('content_types.kind', 'in', kinds!))
          .$if(Boolean(filters.contentTypeHasItemPages), (sub) =>
            sub.where('content_types.item_pages', '=', 1),
          ),
      ),
    );
  }
  if (filters.parentId !== undefined) {
    q =
      filters.parentId === null
        ? q.where('parent_id', 'is', null)
        : q.where('parent_id', '=', filters.parentId);
  }
  if (filters.pathPrefix !== undefined) {
    // Two range bounds on the unique path index, never `like`. `queryPlans.test.ts` asserts the
    // plan, because the wrong form here is a full scan that returns exactly the right rows.
    const { start, end } = descendantPathRange(filters.pathPrefix);
    q = q.where('path', '>', start).where('path', '<', end);
  }
  if (filters.search) {
    const needle = `%${filters.search.toLowerCase()}%`;
    const match = toMatchQuery(filters.search);
    /**
     * Title, path, and the item's own prose through the full-text index.
     *
     * The third term is the whole of what makes this search rather than a title filter, and it is
     * here — in the predicate the listing and its status facets share — rather than in a function of
     * its own, so the admin's cross-type search, the delivery listing's `q`, and the delivery search
     * endpoint all narrow identically. A second implementation for the consumer would be a search
     * that finds a page the CMS cannot, which is the failure SCOPE names.
     *
     * **An uncorrelated `in (subquery)`, which is what keeps this a predicate.** It matters that the
     * `MATCH` sits in a subquery depending on nothing outside it: SQLite materialises it once and
     * feeds a primary-key seek, so the plan is `LIST SUBQUERY` over the FTS index rather than the
     * `SCAN content_items` the old `like '%needle%'` forced on every search. Written as a correlated
     * `EXISTS` — the shape the taxonomy and value filters correctly use — it would instead re-run the
     * whole full-text query once per row, which is worse than the scan it replaced. The difference is
     * invisible in the results and total in the cost.
     *
     * Title and path stay `LIKE`. They are short columns on rows already being visited, and folding
     * them into the FTS index would mean an item's own address changing its search text.
     *
     * `match` is null when the query has no word characters at all (`?`, `!!!`). The prose term is
     * dropped rather than matching nothing, so such a search still finds a page *titled* that.
     */
    q = q.where((eb) =>
      eb.or([
        eb(sql`lower(title)`, 'like', needle),
        eb(sql`lower(path)`, 'like', needle),
        ...(match
          ? [
              eb(
                'content_items.id',
                'in',
                sql<string>`(
                  select t.content_item_id
                  from content_item_fts f
                  join content_item_text t on t.rowid = f.rowid
                  where content_item_fts match ${match}
                )`,
              ),
            ]
          : []),
      ]),
    );
  }

  if (filters.termIds !== undefined) {
    const termIds = filters.termIds;

    /**
     * A correlated EXISTS against the derived assignment index.
     *
     * This is the read `taxonomy_assignments` exists to serve. Tags are authored into `data` and
     * the table is rebuilt from them, which looks like redundancy worth removing — what it buys is
     * exactly this: filtering a list by term without scanning every row and parsing its JSON blob.
     * The index shipped with the taxonomy work and this query never did, so until now the argument
     * for keeping it had no caller to point at.
     *
     * EXISTS rather than `id IN (subquery)` so an item carrying three terms in the branch still
     * counts once, without a DISTINCT over the join.
     */
    q =
      termIds.length === 0
        ? q.where(sql<boolean>`1 = 0`)
        : q.where((eb) =>
            eb.exists(
              eb
                .selectFrom('taxonomy_assignments')
                .select('taxonomy_assignments.content_item_id')
                .whereRef('taxonomy_assignments.content_item_id', '=', 'content_items.id')
                .where('taxonomy_assignments.term_id', 'in', termIds),
            ),
          );
  }

  /**
   * Value filters, each its own correlated EXISTS against the derived value index.
   *
   * EXISTS rather than a join for the reason the taxonomy filter states one line up: a multi-value
   * field satisfying the condition twice must still count the item once. ANDed, because two
   * conditions on a listing mean both — "upcoming *and* in this department".
   */
  for (const filter of filters.valueFilters ?? []) {
    q = q.where((eb) =>
      eb.exists(
        eb
          .selectFrom('content_item_values')
          .select('content_item_values.content_item_id')
          .whereRef('content_item_values.content_item_id', '=', 'content_items.id')
          .where('content_item_values.field_api_id', '=', filter.field)
          .where(
            'content_item_values.value_date',
            filter.operator === 'after' ? '>=' : '<=',
            filter.value,
          ),
      ),
    );
  }

  return q;
}

/**
 * Every column except the two JSON blobs.
 *
 * Spelled out rather than derived, because Kysely needs literals to type the result — and because
 * a column added to `content_items` should have to be considered here rather than silently join a
 * projection that exists to be narrow. `data` and `seo` are the only deliberate omissions.
 */
const ITEM_SUMMARY_COLUMNS = [
  'id',
  'content_type_id',
  'slug',
  'parent_id',
  'path',
  'depth',
  'position',
  'status',
  'title',
  'published_at',
  'publish_at',
  'created_by',
  'updated_by',
  'created_at',
  'updated_at',
] as const;

/** An item without its field values — what a list of links needs and no more. */
export type ContentItemSummary = Omit<ContentItemRow, 'data' | 'seo'>;

/**
 * The count and the page, which are independent and used to be awaited one after the other.
 *
 * Nothing about the total depends on the rows or the rows on the total, so the second `await` was
 * pure serial latency — and against D1 that is a full round trip to another region, on every listing
 * on every page. Same reasoning as the `Promise.all` over the reference loaders in `delivery.ts`.
 *
 * `applyItemFilters` is shared byte for byte with the status facets and stays typed pre-`select`,
 * so the predicate cannot drift between the count and the page it counts.
 */
async function listWith<T>(
  db: Kysely<Database>,
  options: ListItemsOptions,
  page: (query: ReturnType<typeof applyItemFilters>, rankedIds: string[]) => Promise<T[]>,
): Promise<{ rows: T[]; total: number }> {
  const query = applyItemFilters(db.selectFrom('content_items'), options);

  /**
   * The `bm25` order, resolved once here so both list functions get it identically.
   *
   * Only for the ranked path — a listing sorted by `newest` pays nothing. And only the *ordering*
   * depends on it: `applyItemFilters` carries its own FTS predicate, so a page that skipped this
   * would still return exactly the right items, just in path order. That asymmetry is deliberate.
   * Correctness must not rest on a caller remembering a second call, which is the failure mode
   * `termIdsForBranch` has to live with and this does not.
   */
  const ranked =
    listOrder(options) === 'relevance' && options.search
      ? await rankedSearchIds(db, options.search)
      : { ids: [] as string[], truncated: false };

  const [totalRow, rows] = await Promise.all([
    query.select((eb) => eb.fn.countAll<number>().as('count')).executeTakeFirst(),
    page(query, ranked.ids),
  ]);

  return { rows, total: Number(totalRow?.count ?? 0) };
}

/**
 * The order to apply when the caller named none.
 *
 * One place, so a search through the admin and a search through the delivery API cannot come back
 * ordered differently — which would be two searches rather than one.
 */
function listOrder(options: ListItemsOptions): ListOrder {
  return options.sort ?? (options.search ? 'relevance' : 'path');
}

export async function listItems(
  db: Kysely<Database>,
  options: ListItemsOptions = {},
): Promise<{ items: ContentItem[]; total: number }> {
  const { rows, total } = await listWith(db, options, (query, rankedIds) =>
    applyItemSort(
      query.selectAll(),
      listOrder(options),
      options.sortField,
      options.search,
      rankedIds,
    )
      .limit(options.limit ?? 50)
      .offset(options.offset ?? 0)
      .execute(),
  );

  return { items: rows.map(hydrateItem), total };
}

/**
 * The same listing without `data` and `seo`.
 *
 * For every caller that renders titles and links: the delivery API's `/items`, which returns seven
 * scalar fields, and the menu editor's candidate list. Those were reading and JSON-parsing the full
 * content of up to 200 items to show their names — the whole body of every page on the site, over
 * the wire from D1 and through `hydrateItem`, to render a list of anchors.
 *
 * Not a flag on `listItems`, because the two differ in *return type* and a boolean that changes what
 * a function gives back is the kind of thing a caller gets wrong once and never notices — the fields
 * would simply be `undefined`. A caller that needs field values asks for them by calling the other
 * function.
 */
export async function listItemSummaries(
  db: Kysely<Database>,
  options: ListItemsOptions = {},
): Promise<{ items: ContentItemSummary[]; total: number }> {
  const { rows, total } = await listWith(db, options, (query, rankedIds) =>
    applyItemSort(
      query.select([...ITEM_SUMMARY_COLUMNS]),
      listOrder(options),
      options.sortField,
      options.search,
      rankedIds,
    )
      .limit(options.limit ?? 50)
      .offset(options.offset ?? 0)
      .execute(),
  );

  return { items: rows, total };
}

/**
 * Apply one of the named orders.
 *
 * Lives here rather than in `applyItemFilters` on purpose: that function is typed against the
 * *pre-`select`* builder so the listing and the status facets can share it byte for byte, and a
 * facet count has no ordering. Putting `orderBy` there would make the counts pay for a sort they
 * discard, and would make the shared predicate stop being purely a predicate.
 *
 * Every order ends with `path` as a tiebreak so paging is stable — without one, two items sharing a
 * timestamp can swap places between page 1 and page 2 and an item is silently shown twice or not at
 * all.
 *
 * `coalesce` rather than a bare column, and it outlived the portability argument that introduced it
 * (NULL sorting smallest on SQLite and largest on Postgres). What it means now is the thing worth
 * keeping: `newest` is "when did this reach a reader", so an item awaiting its scheduling sweep sorts
 * by when it was written rather than as though it had no date at all.
 */
function applyItemSort<Q extends { orderBy: (...args: any[]) => Q }>(
  query: Q,
  sort: ListOrder,
  sortField?: ListItemsOptions['sortField'],
  search?: string,
  rankedIds?: string[],
): Q {
  /**
   * Relevance: a title band, then `bm25` from the full-text index.
   *
   * **The bands stay; `bm25` breaks ties inside them.** The first instinct on getting a real score
   * was to delete the `CASE` and rank by `bm25` alone, and a test that predates this caught it: the
   * index is built from `content_item_text`, which holds the item's *prose* and not its title, so a
   * page called "Bursaries and aid" whose body never says the word scores nothing at all and sorted
   * below a page merely mentioning it. The old comment's claim was narrower than it read — a `LIKE`
   * cannot rank a *body*, which is what `bm25` fixes; the bands were never about the body. They
   * encode the thing that is still true and still unknowable to the index: a term in the title is
   * what a page is *about*.
   *
   * So the shape is bands first, then score within a band — which is strictly more than either had
   * alone, because the old fifth band (everything matching only in the body) was ordered by `path`,
   * i.e. alphabetically, which is no ranking whatsoever on the largest band.
   *
   * `rankedIds` is the ordinal from `rankedSearchIds`, arriving as **one** delimited parameter read
   * with `instr` — a `CASE` of five hundred branches or an `in` list of five hundred bound values
   * would both be worse, and it is evaluated only on rows the filter already kept. An item outside
   * the ranked window, or matched on its title with no body hit at all, gets 0 from `instr`; the
   * extra `case` is what stops 0 sorting *first* and putting exactly those items above the best
   * matches in their band.
   *
   * Ends with `path` like every other order, so paging stays stable when a hundred pages tie —
   * without it, two items with the same score can swap between page 1 and page 2 and one is shown
   * twice.
   */
  if (sort === 'relevance' && search) {
    const term = search.toLowerCase();
    const contains = `%${term}%`;
    const haystack = rankedIds && rankedIds.length > 0 ? `|${rankedIds.join('|')}|` : '';

    return query
      .orderBy(
        sql`case
              when lower(content_items.title) = ${term} then 0
              when lower(content_items.title) like ${`${term}%`} then 1
              when lower(content_items.title) like ${contains} then 2
              when lower(content_items.path) like ${contains} then 3
              else 4
            end`,
      )
      .orderBy(
        sql`case
              when instr(${haystack}, '|' || content_items.id || '|') = 0 then 1
              else 0
            end`,
      )
      .orderBy(sql`instr(${haystack}, '|' || content_items.id || '|')`)
      .orderBy('path', 'asc');
  }

  // Asked for with no term — which is what a caller gets by passing `sort: 'relevance'` and no
  // search. Falls back rather than erroring, exactly as a field sort with no `sortField` does.
  if (sort === 'relevance') return query.orderBy('path', 'asc');

  /**
   * Ordering by one of the item's own values, through a correlated scalar subquery.
   *
   * A subquery rather than a join, so an item with no value for the field still appears — a join
   * would silently drop every event whose date nobody filled in, which reads as content going
   * missing rather than as a listing being ordered.
   *
   * The column follows the field's kind. Sorted as text, `10` comes before `9`, and a numeric
   * ordering that is wrong in a plausible-looking way is worse than one that is obviously broken.
   *
   * With no `sortField` this falls through to `path` rather than erroring, because the field it
   * named can be deleted from the content type long after a query was saved.
   */
  if ((sort === 'field_asc' || sort === 'field_desc') && sortField) {
    const column =
      sortField.kind === 'date'
        ? sql.ref('value_date')
        : sortField.kind === 'number'
          ? sql.ref('value_num')
          : sql.ref('value_text');

    const direction = sort === 'field_asc' ? sql`asc` : sql`desc`;

    return query
      .orderBy(
        sql`(select ${column} from content_item_values
             where content_item_values.content_item_id = content_items.id
               and content_item_values.field_api_id = ${sortField.apiId}
             limit 1) ${direction}`,
      )
      .orderBy('path', 'asc');
  }

  switch (sort) {
    case 'title':
      return query.orderBy('title', 'asc').orderBy('path', 'asc');
    case 'newest':
      return query.orderBy(sql`coalesce(published_at, created_at)`, 'desc').orderBy('path', 'asc');
    case 'oldest':
      return query.orderBy(sql`coalesce(published_at, created_at)`, 'asc').orderBy('path', 'asc');
    case 'recently_updated':
      return query.orderBy('updated_at', 'desc').orderBy('path', 'asc');
    // Reached only when no `sortField` was supplied — the branch above handles the real case, and
    // falling back beats refusing a query whose date field somebody has since deleted.
    case 'field_asc':
    case 'field_desc':
    case 'path':
      return query.orderBy('path', 'asc');
    default: {
      const exhaustive: never = sort;
      void exhaustive;
      return query.orderBy('path', 'asc');
    }
  }
}

/**
 * How many items sit in each status, under every filter *except* status.
 *
 * The omission is the point, and it is why `status` is excluded at the type level rather than by
 * convention. A status facet exists to answer "what would I get if I switched to Draft?", so
 * counting within the current status filter would answer with the number already on screen and
 * zero everywhere else. Statuses with no items are returned as 0 rather than omitted, so callers
 * can render a complete list without treating a missing key as a special case.
 */
export async function countItemsByStatus(
  db: Kysely<Database>,
  filters: Omit<ItemFilters, 'status'> = {},
): Promise<Record<ContentStatus, number>> {
  const rows = await applyItemFilters(db.selectFrom('content_items'), filters)
    .select('status')
    .select((eb) => eb.fn.countAll<number>().as('n'))
    .groupBy('status')
    .execute();

  const counts: Record<ContentStatus, number> = {
    draft: 0,
    in_review: 0,
    scheduled: 0,
    published: 0,
    archived: 0,
  };

  for (const row of rows) {
    // A row can carry a status this build does not know if the database is ahead of the code.
    // Dropping it keeps the shape honest instead of inventing a key nobody can render.
    if (Object.hasOwn(counts, row.status)) counts[row.status] = Number(row.n);
  }

  return counts;
}

export async function getItem(
  db: Kysely<Database>,
  id: string,
): Promise<ContentItem | undefined> {
  const row = await db
    .selectFrom('content_items')
    .selectAll()
    .where('id', '=', id)
    .executeTakeFirst();
  return row ? hydrateItem(row) : undefined;
}

/**
 * Resolve a request path to a content item in a single indexed lookup.
 *
 * This is the hot path — every public page view runs it — which is why `path` is a unique indexed
 * column rather than something reconstructed by walking parents at request time.
 */
/**
 * The SQL condition for "a visitor may see this".
 *
 * One expression, used by every reader — the rule that decides what the public sees is exactly the
 * kind that must not be implemented twice. It lives here rather than in `scheduler.ts` because
 * `scheduler.ts` already depends on this module, and the reverse would close the loop.
 *
 * A `scheduled` item whose time has passed is included whether or not a sweep has run: that is
 * what makes "goes live at 9am" true on a deployment where nobody wired up a cron.
 */
export function visibleToPublic(eb: any) {
  return eb.or([
    eb('status', '=', 'published'),
    eb.and([
      eb('status', '=', 'scheduled'),
      eb('publish_at', 'is not', null),
      eb('publish_at', '<=', now()),
    ]),
  ]);
}

export async function getItemByPath(
  db: Kysely<Database>,
  path: string,
  options: { publishedOnly?: boolean; routableOnly?: boolean } = {},
): Promise<ContentItem | undefined> {
  let query = db.selectFrom('content_items').selectAll().where('path', '=', normalizePath(path));

  /**
   * Skip an item whose type has no pages of its own — the delivery resolver's caller.
   *
   * In the same statement rather than checked afterwards, because "afterwards" means loading the
   * content type to ask, which is a second round trip on the hot path of every page view. The item
   * still *has* this path: it is how the admin addresses the row and what a preview token would be
   * scoped to. What it does not have is anything served at it.
   */
  if (options.routableOnly) {
    query = query.where((eb) =>
      eb.exists(
        eb
          .selectFrom('content_types')
          .select('content_types.id')
          .whereRef('content_types.id', '=', 'content_items.content_type_id')
          .where('content_types.item_pages', '=', 1),
      ),
    );
  }

  /**
   * A scheduled item whose time has passed counts as visible, whether or not a sweep has run yet.
   *
   * That is what makes "goes live at 9am" true on a deployment where nobody wired up a cron —
   * which is every deployment on its first day. The sweep then makes the *stored* status agree;
   * without this rule, a missed cron would silently hold a launch.
   */
  if (options.publishedOnly !== false) query = query.where(visibleToPublic);

  const row = await query.executeTakeFirst();
  return row ? hydrateItem(row) : undefined;
}

/**
 * Just enough of an item to build its cache validator: which item a path names, and its version.
 *
 * The delivery route used to answer a conditional request by resolving the whole page — every
 * query, every loader, the full payload — and then discarding the body when the ETag matched. That
 * saves bytes, and bytes are the part Cloudflare does not charge for; D1 bills rows *read*, so a
 * 304 cost exactly what a 200 did. This is the one indexed lookup that answers the question the
 * validator actually asks.
 *
 * It has to share `visibleToPublic` with `getItemByPath`, and that is the whole reason it lives
 * beside it rather than in the route: a validator computed under a different visibility rule than
 * the payload would let a conditional request 304 against a version a visitor may not see.
 *
 * `updated_at` is the version because every path that changes what a page renders stamps it —
 * an edit, a publish, a status change, a cascading move, a release applying a staged version. The
 * one thing it does not cover is a reusable block edited in the library, which is unchanged here and
 * bounded by the shared TTL; see `deliveryCache`.
 */
export async function getItemVersionByPath(
  db: Kysely<Database>,
  path: string,
  options: { publishedOnly?: boolean; routableOnly?: boolean } = {},
): Promise<{ id: string; updatedAt: string } | undefined> {
  let query = db
    .selectFrom('content_items')
    .select(['id', 'updated_at'])
    .where('path', '=', normalizePath(path));

  if (options.publishedOnly !== false) query = query.where(visibleToPublic);

  /**
   * And the same routability rule, for the reason the visibility one is shared: a validator
   * computed under different rules from the payload would answer 304 for a path the full request
   * answers 404 — a client holding a cached copy of a page that no longer exists, revalidating it
   * successfully forever.
   */
  if (options.routableOnly) {
    query = query.where((eb) =>
      eb.exists(
        eb
          .selectFrom('content_types')
          .select('content_types.id')
          .whereRef('content_types.id', '=', 'content_items.content_type_id')
          .where('content_types.item_pages', '=', 1),
      ),
    );
  }

  const row = await query.executeTakeFirst();
  return row ? { id: row.id, updatedAt: row.updated_at } : undefined;
}

/** Look up a redirect for a path that no longer resolves. */
export async function getRedirect(
  db: Kysely<Database>,
  path: string,
): Promise<{ to: string; status: number } | undefined> {
  const row = await db
    .selectFrom('redirects')
    .select(['to_path', 'status_code'])
    .where('from_path', '=', normalizePath(path))
    .executeTakeFirst();

  return row ? { to: row.to_path, status: row.status_code } : undefined;
}

export async function getChildren(
  db: Kysely<Database>,
  parentId: string | null,
): Promise<ContentItem[]> {
  const query = db.selectFrom('content_items').selectAll().orderBy('position').orderBy('title');
  const rows = await (parentId === null
    ? query.where('parent_id', 'is', null)
    : query.where('parent_id', '=', parentId)
  ).execute();

  return rows.map(hydrateItem);
}

/**
 * Read an item and every descendant, in one recursive query.
 *
 * `WITH RECURSIVE` works identically on both drivers, which is what makes cascading
 * moves implementable rather than something to special-case away.
 */
export async function getSubtree(db: Kysely<Database>, rootId: string): Promise<SubtreeNode[]> {
  const result = await sql<SubtreeNode>`
    WITH RECURSIVE subtree(id, path, depth) AS (
      SELECT id, path, depth FROM content_items WHERE id = ${rootId}
      UNION ALL
      SELECT c.id, c.path, c.depth
      FROM content_items c
      JOIN subtree s ON c.parent_id = s.id
    )
    SELECT id, path, depth FROM subtree
  `.execute(db);

  return result.rows;
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Block type schemas, loaded only when this item actually places a block.
 *
 * Two callers on every write — `validateItemData` checks a block's contents against them, and the
 * search index walks those contents to flatten their prose — so it is loaded once and passed to
 * both rather than fetched twice for the same map.
 *
 * The gate is the one `resolveDelivery` already applies on the read path, and it is **data-driven
 * rather than schema-driven** for the same reason: asking "does this content type offer a block
 * field" is answered yes by every type that offers blocks at all, while asking what this item
 * actually holds is answered no by the page nobody put one on. An empty map is the right answer for
 * that page in both directions — there is nothing to validate and nothing to index.
 */
async function blockTypesFor(
  db: Kysely<Database>,
  fields: FieldRow[],
  data: Record<string, unknown>,
): Promise<Map<string, ContentTypeWithFields>> {
  const placesBlocks = fields.some((field) => {
    if (field.type !== 'block') return false;
    const value = data[field.api_id];
    return Array.isArray(value) && value.length > 0;
  });

  return placesBlocks ? blockTypeRegistry(db) : new Map();
}

export interface CreateItemInput {
  contentTypeId: string;
  title: string;
  slug?: string;
  parentId?: string | null;
  status?: ContentStatus;
  data?: Record<string, unknown>;
  seo?: SeoData;
  /** When a `scheduled` item should go live. ISO 8601. */
  publishAt?: string | null;
  userId?: string | null;
}

export async function createItem(
  handle: TaprootDb,
  contentType: ContentTypeRow,
  fields: FieldRow[],
  input: CreateItemInput,
): Promise<ContentItem> {
  const { db } = handle;

  /**
   * A block type has no addressable instances, so it can never have a content item.
   *
   * Guarded here rather than only in the admin because block types share a table with content
   * types: a POST carrying a block type's id would otherwise create an item with no URL, invisible
   * in every list that filters blocks out.
   */
  if (contentType.kind === 'block') {
    throw new ContentItemError(
      `"${contentType.name}" is a block type. Blocks are placed into a block field on a content ` +
        `item; they do not have items of their own.`,
      'invalid_parent',
    );
  }

  // A singleton exists exactly once — that is the whole point of the kind.
  if (contentType.kind === 'singleton') {
    const existing = await db
      .selectFrom('content_items')
      .select('id')
      .where('content_type_id', '=', contentType.id)
      .executeTakeFirst();

    if (existing) {
      throw new ContentItemError(
        `"${contentType.name}" is a singleton and already has an item. Edit the existing one.`,
        'singleton_exists',
      );
    }
  }

  const parentId = contentType.kind === 'page' ? (input.parentId ?? null) : null;
  const parent = parentId ? await getItem(db, parentId) : undefined;
  if (parentId && !parent) {
    throw new ContentItemError(`Parent item ${parentId} not found.`, 'invalid_parent');
  }

  // Loaded once and used twice: validation needs it to check a block's contents, and the text index
  // needs it to walk them. A second call would be a second query for the same map.
  const blockTypes = await blockTypesFor(db, fields, input.data ?? {});

  const validation = validateItemData(fields, input.data ?? {}, { blockTypes });
  if (!validation.success) {
    throw new ContentItemError('Content failed validation.', 'validation_failed', validation.errors);
  }

  // Still read, but only for `position` below — the slug comes from the path namespace now, because
  // sibling slugs and addressable paths are not the same set. See `uniquePathSlug`.
  const siblings = await siblingSlugs(db, contentType.id, parentId);
  // `||` rather than `??`: an editor who leaves the slug blank sends an empty string, not
  // undefined, and `??` would let it through to `uniquePathSlug` — which slugifies it to nothing and
  // falls back to the literal "item". Blank means "derive it from the title".
  const slug = await uniquePathSlug(
    db,
    contentType,
    parent?.path ?? null,
    blankToUndefined(input.slug) || slugify(input.title),
  );
  const path = resolveItemPath(contentType, parent?.path ?? null, slug);

  const timestamp = now();
  const status = input.status ?? 'draft';
  const row: ContentItemRow = {
    id: newId(),
    content_type_id: contentType.id,
    slug,
    parent_id: parentId,
    path,
    depth: parent ? parent.depth + 1 : 0,
    position: siblings.length,
    status,
    title: input.title,
    data: stringifyJson(validation.data ?? {}),
    seo: stringifyJson(input.seo ?? {}),
    published_at: status === 'published' ? timestamp : null,
    // Set through the scheduling path rather than at creation: an item is not scheduled until
    // somebody picks a time, and a create that lands straight in `scheduled` has none yet.
    publish_at: input.publishAt ?? null,
    created_by: input.userId ?? null,
    updated_by: input.userId ?? null,
    created_at: timestamp,
    updated_at: timestamp,
  };

  const assignments = await planAssignmentIndex(db, row.id, fields, validation.data ?? {});
  assertTermsExist(assignments.missing);
  // Beside the taxonomy index and never apart from it: two derived tables rebuilt at different
  // write points is one of them going quietly stale.
  const derived = planDerivedIndexes(db, row.id, fields, validation.data ?? {}, { blockTypes });

  // Batched rather than a bare insert so the item, its first revision, and its taxonomy index
  // cannot diverge — an item whose history begins one save late is a gap that can never be
  // reconstructed, and a stale index row is invisible until it wrongly answers a filtered listing.
  await handle.batch([
    db.insertInto('content_items').values(row),
    ...assignments.statements,
    ...derived,
    // The create snapshot goes the same way as every later one: a type that keeps no history does
    // not start one here either, or its first save would leave a single orphan revision that no
    // screen offers to restore from.
    ...(typeKeepsRevisions(contentType)
      ? [
          buildRevisionStatement(db, {
            contentItemId: row.id,
            revisionNumber: 1,
            title: row.title,
            slug: row.slug,
            path: row.path,
            status,
            data: validation.data ?? {},
            seo: input.seo ?? {},
            reason: 'create',
            userId: input.userId ?? null,
            timestamp,
          }),
        ]
      : []),
  ]);

  return hydrateItem(row);
}

export interface UpdateItemInput {
  title?: string;
  slug?: string;
  parentId?: string | null;
  status?: ContentStatus;
  data?: Record<string, unknown>;
  seo?: SeoData;
  /**
   * When a `scheduled` item should go live. ISO 8601, or null to clear.
   *
   * Cleared automatically whenever the status leaves `scheduled` — see the write below. A stale
   * time left on a published page is a booby trap: reschedule it later and it goes live in the
   * past, which is to say immediately.
   */
  publishAt?: string | null;
  userId?: string | null;
  /**
   * How the resulting revision should be labelled. Defaults to `save`.
   *
   * Only `restoreRevision` sets this, to mark that a save came from restoring earlier content
   * rather than from someone editing. It is on the input rather than a separate code path because
   * a restore *is* an ordinary update — same validation, same path cascade, same redirects.
   */
  revisionReason?: RevisionReason;
  /** The revision number being restored. Meaningful only with `revisionReason: 'restore'`. */
  restoredFrom?: number | null;
}

/**
 * Update an item, cascading path changes to its descendants.
 *
 * When the slug or parent changes, every descendant's path changes with it, and each moved path
 * gets a redirect written automatically. Doing that by hand is what people forget, which is why
 * it happens here rather than being left to whoever remembers.
 *
 * The whole rewrite is submitted as one atomic batch, so a partially-renamed tree is not a state
 * the database can end up in.
 */
export async function updateItem(
  handle: TaprootDb,
  contentType: ContentTypeRow,
  fields: FieldRow[],
  id: string,
  input: UpdateItemInput,
): Promise<ContentItem> {
  const { db } = handle;

  const existing = await getItem(db, id);
  if (!existing) throw new ContentItemError(`Content item ${id} not found.`, 'not_found');

  let data = existing.data;
  let blockTypes: Map<string, ContentTypeWithFields>;

  if (input.data !== undefined) {
    blockTypes = await blockTypesFor(db, fields, input.data);

    const validation = validateItemData(fields, input.data, { blockTypes });
    if (!validation.success) {
      throw new ContentItemError(
        'Content failed validation.',
        'validation_failed',
        validation.errors,
      );
    }
    data = validation.data ?? {};
  } else {
    /**
     * No new content — and the registry is still needed, because the derived indexes below are
     * rebuilt from what is stored whether or not this save changed it. Without it, publishing a page
     * would walk its blocks with no schemas to walk them by and quietly strip every block's prose
     * out of the search index, on the one action most likely to be the last one somebody takes.
     */
    blockTypes = await blockTypesFor(db, fields, data);
  }

  const timestamp = now();
  const status = input.status ?? existing.status;

  const parentChanged =
    input.parentId !== undefined && (input.parentId ?? null) !== existing.parent_id;

  // A blank slug on update means "leave it alone", not "regenerate". Regenerating would silently
  // move the page and write a redirect every time someone saved with the field cleared.
  const submittedSlug = blankToUndefined(input.slug);
  const desiredSlug = submittedSlug ? slugify(submittedSlug) || existing.slug : existing.slug;
  const slugChanged = desiredSlug !== existing.slug;

  const statements: BatchStatement[] = [];
  let slug = existing.slug;
  let path = existing.path;
  let parentId = existing.parent_id;
  let depth = existing.depth;

  if ((parentChanged || slugChanged) && contentType.kind !== 'singleton') {
    const subtree = await getSubtree(db, id);
    const nextParentId = parentChanged ? (input.parentId ?? null) : existing.parent_id;

    // Re-parenting a node under its own descendant would detach the whole subtree from the site.
    if (wouldCreateCycle(subtree, nextParentId)) {
      throw new ContentItemError(
        'A content item cannot be moved underneath itself or one of its own descendants.',
        'cycle',
      );
    }

    const parent = nextParentId ? await getItem(db, nextParentId) : undefined;
    if (nextParentId && !parent) {
      throw new ContentItemError(`Parent item ${nextParentId} not found.`, 'invalid_parent');
    }

    slug = await uniquePathSlug(db, contentType, parent?.path ?? null, desiredSlug, id);
    path = resolveItemPath(contentType, parent?.path ?? null, slug);
    parentId = nextParentId;
    depth = parent ? parent.depth + 1 : 0;

    const rewrites = computeSubtreeRewrite(subtree, id, path);
    statements.push(
      ...buildRewriteStatements(db, rewrites, { rootId: id, parentId, slug, timestamp }),
    );
    statements.push(...buildRedirectStatements(db, rewrites, timestamp));
  }

  const title = input.title ?? existing.title;
  const seo = input.seo ?? existing.seo;

  statements.push(
    db
      .updateTable('content_items')
      .set({
        title,
        status,
        data: stringifyJson(data),
        seo: stringifyJson(seo),
        published_at:
          status === 'published' ? (existing.published_at ?? timestamp) : existing.published_at,
        /**
         * Cleared the moment the status leaves `scheduled`.
         *
         * A stale time left behind on a published or archived page is a booby trap: schedule it
         * again months later, and the sweep sees a `publish_at` in the past and takes it live
         * immediately. Tying the value's lifetime to the status it belongs to is what stops that.
         */
        /**
         * `undefined` means "not provided"; `null` means "clear it". `??` cannot tell them apart,
         * so writing it that way silently ignored a request to remove the date — the same class of
         * bug as deriving a PATCH schema with `.partial()` and having a `.default()` survive.
         */
        publish_at:
          status === 'scheduled'
            ? input.publishAt !== undefined
              ? input.publishAt
              : existing.publish_at
            : null,
        updated_by: input.userId ?? existing.updated_by,
        updated_at: timestamp,
      })
      .where('id', '=', id),
  );

  /**
   * Append a revision describing the item as it will be *after* this save.
   *
   * Both reads happen here, before the batch is submitted, because a batch cannot read its own
   * writes. Two cases skip the append:
   *
   * - Nothing about the authored content changed, so the previous revision already says this.
   * - ...unless the item has no revisions at all, which means it predates this table. Backfilling
   *   its current state gives the history a floor to diff against instead of starting blank.
   */
  const assignments = await planAssignmentIndex(db, id, fields, data);
  assertTermsExist(assignments.missing);
  statements.push(...assignments.statements);
  /**
   * The value and text indexes, rebuilt from the same `data` in the same batch.
   *
   * Note this is the *item's own* update path only. The cascading path move below rewrites
   * descendants' `path` and touches nothing in their `data`, so reindexing them would add rows to a
   * batch that already carries a statement per descendant — for values and prose that cannot have
   * changed.
   */
  statements.push(...planDerivedIndexes(db, id, fields, data, { blockTypes }));

  /*
   * A type can turn revisions off, and then no save appends one.
   *
   * The existing rows are left alone — `listRevisions` keeps answering, and a type switched off
   * after a year of edits still has its year of history to read. Erasing them would make a
   * content-type setting a silent delete of the only record of what an item used to say.
   *
   * Note what this does *not* touch: the item still has a status, still moves through the same
   * transition graph, and can still be staged in a release. There is one workflow graph and no
   * per-type variant of it.
   */
  const keepsRevisions = typeKeepsRevisions(contentType);
  // Not asked for at all when they are off — one fewer round trip on every save of such a type.
  const sequence = keepsRevisions ? await revisionSequence(db, id) : { count: 0, latest: 0 };
  const after = { title, slug, status, data, seo };
  if (keepsRevisions && (sequence.count === 0 || !snapshotIsUnchanged(existing, after))) {
    statements.push(
      buildRevisionStatement(db, {
        contentItemId: id,
        revisionNumber: sequence.latest + 1,
        ...after,
        path,
        reason: input.revisionReason ?? 'save',
        restoredFrom: input.restoredFrom ?? null,
        userId: input.userId ?? existing.updated_by,
        timestamp,
      }),
    );
  }

  await handle.batch(statements);

  return {
    ...existing,
    title,
    slug,
    path,
    parent_id: parentId,
    depth,
    status,
    data,
    seo,
    updated_at: timestamp,
  };
}

/**
 * Restore an item to an earlier revision.
 *
 * Deliberately routed through `updateItem` rather than writing the old row back directly. A
 * revision stores the slug, so restoring one can move the page — and that has to cascade to every
 * descendant's path and write the redirects, exactly as an ordinary rename does. Writing the
 * snapshot back verbatim would restore the content and quietly corrupt the tree.
 *
 * The restore appends a new revision rather than truncating the log back to the restored point.
 * History stays append-only, so restoring the wrong revision is itself undoable.
 */
export async function restoreRevision(
  handle: TaprootDb,
  contentType: ContentTypeRow,
  fields: FieldRow[],
  itemId: string,
  revisionId: string,
  userId?: string | null,
): Promise<ContentItem> {
  const revision = await getRevision(handle.db, revisionId);
  if (!revision) {
    throw new RevisionError(`Revision ${revisionId} not found.`, 'not_found');
  }

  // Without this an editor could restore one item's content over another's by pasting an id.
  if (revision.content_item_id !== itemId) {
    throw new RevisionError(
      'That revision belongs to a different content item.',
      'wrong_item',
    );
  }

  return updateItem(handle, contentType, fields, itemId, {
    title: revision.title,
    slug: revision.slug,
    status: revision.status,
    data: revision.data,
    seo: revision.seo,
    userId,
    revisionReason: 'restore',
    restoredFrom: revision.revision_number,
  });
}

/**
 * Put one sibling group in a new order.
 *
 * **`position` had no write path at all until this.** `createItem` set it to `siblings.length` and
 * nothing ever changed it again, so the order `resolveDelivery` hands a consumer as an item's
 * children was permanently the order somebody happened to create them in. The only fix available to
 * an editor was to delete a page and make it again, which loses its revisions, its id and every link
 * pointing at it.
 *
 * ## Why this takes a whole level, and rejects a partial one
 *
 * `position` means nothing except relative to siblings, so a reorder is inherently per-level — the
 * same reasoning `reorderMenuItems` records. This goes one step further and requires the ids to be
 * **exactly** the parent's children: no missing ones, no extras.
 *
 * A subset would be silently wrong. Positions here are assigned `0..n-1`, so reordering three of a
 * parent's eight children hands those three the positions the first three already hold, and the
 * result is a level with duplicate positions ordered by the `title` tiebreak — which looks like the
 * drag simply not working, on some rows, sometimes.
 *
 * Exactness also settles the concurrency case for free, and it is a real one: two editors on one
 * section of a site, one adds a page while the other drags. The dragger's list predates the insert,
 * so it is refused and their screen reloads, rather than the new page being shuffled to the end of a
 * level it was never part of.
 *
 * ## What it deliberately does not do
 *
 * **No revision.** A revision snapshots `title`, `slug`, `status`, `data` and `seo` — position is
 * in none of them, so restoring one has never restored an order and writing a revision here would
 * append history entries that say nothing and restore nothing.
 *
 * **No path rewrite, and that asymmetry is the whole reason this is a separate function rather than
 * a key on `UpdateItemInput`.** Reordering siblings touches one integer per row: no slug changes,
 * no descendant paths move, no redirects are written. Re-parenting does all three, for the item and
 * every descendant. Keeping them apart is what lets an editor's screen offer dragging as the cheap,
 * fluid act and re-parenting as an explicit one — a drag that could silently rewrite forty URLs and
 * write forty redirects is not a drag anybody should be offered.
 *
 * ## The parent's own timestamp moves
 *
 * `updated_at` is bumped on the reordered children **and on the parent**, which is not tidiness.
 * The parent's delivery response carries its children in this order, and `getItemVersionByPath`
 * answers a conditional request from `updated_at` alone — so leaving the parent's stamp still would
 * mean a cached copy of the parent revalidating, being told 304, and having its freshness renewed
 * against an order that has changed. RFC 9111 §4.3.4 makes that unbounded rather than capped at the
 * TTL, which is the same trap `reusableBlockLibraryVersion` exists to close one feature along.
 */
export async function reorderSiblings(
  handle: TaprootDb,
  parentId: string | null,
  orderedIds: string[],
): Promise<void> {
  const children = await getChildren(handle.db, parentId);

  const known = new Set(children.map((child) => child.id));
  const requested = new Set(orderedIds);

  const exact =
    orderedIds.length === children.length &&
    requested.size === orderedIds.length &&
    children.every((child) => requested.has(child.id));

  if (!exact) {
    throw new ContentItemError(
      'That order does not match this item\'s children. Reload the page and try again.',
      'stale_order',
    );
  }

  // Nothing to write, and skipping it keeps a no-op drag from moving the parent's ETag and
  // invalidating every cached copy of a page whose order did not change.
  const unchanged = children.every((child, index) => child.id === orderedIds[index]);
  if (unchanged) return;

  const timestamp = now();
  const statements: BatchStatement[] = orderedIds.map((id, index) =>
    handle.db
      .updateTable('content_items')
      .set({ position: index, updated_at: timestamp })
      .where('id', '=', id),
  );

  if (parentId !== null) {
    statements.push(
      handle.db
        .updateTable('content_items')
        .set({ updated_at: timestamp })
        .where('id', '=', parentId),
    );
  }

  await handle.batch(statements);
}

/**
 * What has to be cleared before this item can be deleted, and what merely changes if it is.
 *
 * Same shape and same reasoning as `contentTypeDeleteBlockers`: one function that both the guard
 * and the screen read, so a screen cannot work out for itself that a delete would succeed and then
 * be refused. Blockers are phrased as standalone clauses so they read correctly both bulleted and
 * after the error's `Cannot delete X:` prefix.
 *
 * The split between the two lists is the difference between a broken invariant and a consequence.
 * Descendants block, because `parent_id` is `ON DELETE SET NULL` and the delete would leave them
 * at root with a `path` and `depth` still describing where they used to be — the materialised path
 * and the tree would disagree, which nothing downstream expects. A menu entry or an incoming
 * relation is a consequence: both already degrade visibly and on purpose, so the editor should be
 * told rather than stopped.
 */
export interface ItemDeleteImpact {
  blockers: string[];
  warnings: string[];
}

export async function itemDeleteImpact(
  db: Kysely<Database>,
  itemId: string,
): Promise<ItemDeleteImpact> {
  const blockers: string[] = [];
  const warnings: string[] = [];

  const item = await db
    .selectFrom('content_items')
    .select(['id', 'path'])
    .where('id', '=', itemId)
    .executeTakeFirst();

  if (!item) return { blockers: ['it no longer exists.'], warnings };

  const children = await db
    .selectFrom('content_items')
    .select((eb) => eb.fn.countAll<number>().as('count'))
    .where('parent_id', '=', itemId)
    .executeTakeFirst();

  const childCount = Number(children?.count ?? 0);
  if (childCount > 0) {
    blockers.push(
      `${childCount} item(s) sit beneath it. Move or delete them first, or they are left at the ` +
        'top level with URLs that still describe the old position.',
    );
  }

  /**
   * Staged in an unpublished release: a blocker, not a warning.
   *
   * `release_items.content_item_id` cascades, so deleting the item would take its staged version
   * with it and the release would simply publish without that page — no broken row, no message,
   * and nobody notices until the launch is missing something. That is the distinction this list
   * turns on: a menu entry and an incoming relation *degrade visibly*, which is why they only warn.
   *
   * Queried directly rather than through `openReleasesForItem`, which would make `releases.ts` and
   * this module import each other. The dependency runs one way — releases reads items — for the
   * same reason `visibleToPublic` lives here rather than in `scheduler.ts`.
   */
  const releases = await db
    .selectFrom('release_items')
    .innerJoin('releases', 'releases.id', 'release_items.release_id')
    .select(['releases.name as name'])
    .where('release_items.content_item_id', '=', itemId)
    .where('releases.status', '!=', 'published')
    .execute();

  if (releases.length > 0) {
    const names = [...new Set(releases.map((release) => release.name))].join(', ');
    blockers.push(
      `it is staged in ${releases.length} unpublished release(s): ${names}. Remove it from them ` +
        'first, or the release publishes without it and nothing says so.',
    );
  }

  const menuEntries = await db
    .selectFrom('menu_items')
    .innerJoin('menus', 'menus.id', 'menu_items.menu_id')
    .select(['menus.name as menu_name'])
    .where('menu_items.content_item_id', '=', itemId)
    .execute();

  if (menuEntries.length > 0) {
    const names = [...new Set(menuEntries.map((entry) => entry.menu_name))].join(', ');
    warnings.push(
      `${menuEntries.length} menu item(s) point at it, in: ${names}. They stay in the admin as ` +
        'broken entries and stop rendering on the site.',
    );
  }

  const referencing = await itemsReferencing(db, itemId, 20);
  if (referencing.length > 0) {
    const titles = referencing
      .slice(0, 5)
      .map((reference) => reference.title)
      .join(', ');
    const more = referencing.length > 5 ? `, and ${referencing.length - 5} more` : '';
    warnings.push(
      `${referencing.length} item(s) reference it through a relation field: ${titles}${more}. ` +
        'Those fields keep the id and show it as missing.',
    );
  }

  return { blockers, warnings };
}

export class ItemError extends Error {
  constructor(
    message: string,
    readonly code: 'in_use',
  ) {
    super(message);
    this.name = 'ItemError';
  }
}

export async function deleteItem(handle: TaprootDb, id: string): Promise<void> {
  const { blockers } = await itemDeleteImpact(handle.db, id);
  if (blockers.length > 0) {
    // Enforced here rather than only in the route, so the REST API cannot do what the admin
    // refuses. The editor is not the boundary.
    throw new ItemError(`Cannot delete this item: ${blockers[0]}`, 'in_use');
  }

  /**
   * Batched, so the full-text retraction and the delete cannot land apart.
   *
   * Every other derived table cascades on `content_items.id` and needs nothing here.
   * `content_item_fts` cannot: it is an external-content FTS5 index with no foreign key to cascade
   * along, and retracting an item's terms needs the text — which the cascade is about to take away.
   * So it goes first, in the same batch. See `planTextIndexDelete`.
   */
  await handle.batch([
    planTextIndexDelete(handle.db, id),
    handle.db.deleteFrom('content_items').where('id', '=', id),
  ]);
}

/** One item pointing at another through a `relation` field. */
export interface IncomingReference {
  id: string;
  title: string;
  path: string;
  status: ContentStatus;
  /** The relation field on the referring item that points here. */
  fieldApiId: string;
  fieldLabel: string;
  /**
   * What this side of the relationship is called, from the field's `reverseLabel`.
   *
   * The config has collected this since the field type was designed and nothing ever read it,
   * which is what made the reverse side of a relation a promise rather than a feature.
   */
  reverseLabel: string | null;
  contentTypeName: string;
}

/**
 * Which items point at this one through a relation field.
 *
 * The reverse side of `relation`, which SCOPE names as the thing Wolly gets wrong. Without it a
 * relation is one-directional in practice: an editor looking at a page has no way to know what
 * depends on it, and finds out by deleting it.
 *
 * Two steps, and the first is what makes the second honest. Relation targets live in another
 * type's JSON `config`, so the set of fields that *could* point here is found by reading the
 * `fields` table; only then is `content_items.data` searched, narrowed to the types that own one
 * of those fields. A `LIKE` for the bare id across every item would also match the id sitting in
 * a text field or a block's media reference, and would report a relationship that does not exist.
 *
 * Same trade as `countBlockUsage`: relation values live inside a JSON blob and have no rows of
 * their own, so this cannot be an indexed join. It runs on one screen and when deleting an item,
 * and is bounded by `limit`.
 */
export async function itemsReferencing(
  db: Kysely<Database>,
  itemId: string,
  limit = 50,
): Promise<IncomingReference[]> {
  const item = await db
    .selectFrom('content_items')
    .select('content_type_id')
    .where('id', '=', itemId)
    .executeTakeFirst();

  if (!item) return [];

  const relationFields = await db
    .selectFrom('fields')
    .selectAll()
    .where('type', '=', 'relation')
    .execute();

  const pointingHere = relationFields.filter((field) => {
    try {
      const config = JSON.parse(field.config) as { targetContentTypeId?: string | null };
      return config.targetContentTypeId === item.content_type_id;
    } catch {
      return false;
    }
  });

  if (pointingHere.length === 0) return [];

  const ownerTypeIds = [...new Set(pointingHere.map((field) => field.content_type_id))];

  const candidates = await db
    .selectFrom('content_items')
    .innerJoin('content_types', 'content_types.id', 'content_items.content_type_id')
    .select([
      'content_items.id as id',
      'content_items.title as title',
      'content_items.path as path',
      'content_items.status as status',
      'content_items.content_type_id as content_type_id',
      'content_items.data as data',
      'content_types.name as content_type_name',
    ])
    .where('content_items.content_type_id', 'in', ownerTypeIds)
    .where('content_items.data', 'like', `%${itemId}%`)
    .orderBy('content_items.path')
    .execute();

  const references: IncomingReference[] = [];

  for (const candidate of candidates) {
    const data = parseJson<Record<string, unknown>>(candidate.data, {});

    for (const field of pointingHere) {
      if (field.content_type_id !== candidate.content_type_id) continue;

      // Checked against the field's own key rather than trusting the `LIKE`, which is only a
      // prefilter — it matches the id anywhere in the blob, including places that are not a
      // relation to it at all.
      const stored = data[field.api_id];
      const points = Array.isArray(stored) ? stored.includes(itemId) : stored === itemId;
      if (!points) continue;

      let reverseLabel: string | null = null;
      try {
        reverseLabel =
          (JSON.parse(field.config) as { reverseLabel?: string }).reverseLabel ?? null;
      } catch {
        // A malformed config costs the group its name, not the reference its visibility.
      }

      references.push({
        id: candidate.id,
        title: candidate.title,
        path: candidate.path,
        status: candidate.status as ContentStatus,
        fieldApiId: field.api_id,
        fieldLabel: field.label,
        reverseLabel,
        contentTypeName: candidate.content_type_name,
      });

      if (references.length >= limit) return references;
    }
  }

  return references;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/**
 * Reject a save that references terms which do not exist.
 *
 * Caught here rather than left to the foreign key so the caller gets per-field messages in the
 * same shape as every other validation failure — and so the whole batch is never submitted, rather
 * than failing at statement nine of twelve with a constraint error nobody can act on.
 */
function assertTermsExist(missing: Record<string, string[]>): void {
  const apiIds = Object.keys(missing);
  if (apiIds.length === 0) return;

  throw new ContentItemError(
    'Content failed validation.',
    'validation_failed',
    Object.fromEntries(
      apiIds.map((apiId) => [
        apiId,
        [
          missing[apiId]!.length === 1
            ? 'That term no longer exists. It may have been deleted while you were editing.'
            : 'Some of those terms no longer exist. They may have been deleted while you were editing.',
        ],
      ]),
    ),
  );
}

/**
 * Treat a blank or whitespace-only string as absent.
 *
 * Forms submit empty strings where an API client would send nothing at all, and the two must mean
 * the same thing to the services underneath.
 */
function blankToUndefined(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/** Where an item's path comes from depends on its type's kind. */
export function resolveItemPath(
  contentType: ContentTypeRow,
  parentPath: string | null,
  slug: string,
): string {
  switch (contentType.kind) {
    case 'page':
      return buildPath(parentPath, slug);
    case 'collection':
      return buildCollectionPath(contentType.url_prefix, slug);
    case 'singleton':
      // Singletons are edited and rendered through other content, never routed to directly.
      return `/__singleton/${contentType.api_id}`;
    case 'block':
      // Unreachable through the normal path: `createItem` refuses a block type before it gets
      // here. Kept explicit rather than folded into the default so the exhaustiveness check keeps
      // working, and so reaching it names the actual mistake.
      throw new Error(
        `Block types have no items and therefore no path. "${contentType.api_id}" should never ` +
          `have reached path resolution.`,
      );
    default: {
      const exhaustive: never = contentType.kind;
      throw new Error(`Unhandled content type kind: ${String(exhaustive)}`);
    }
  }
}

/**
 * Pick a slug whose resulting **path** is free, which is the namespace that actually has to be unique.
 *
 * `siblingSlugs` answers a different question — who sits beside this item in the admin — and the two
 * disagree in both directions. A collection item and a root page are not siblings and can still
 * collide (see `0024_root_slug_scope`), and two root items of two different `page`-kind types *are*
 * addressed identically (`/apply` twice) while being siblings of neither. Disambiguating against
 * `path` covers both, because `content_items_path_unique` is the constraint that was going to be
 * enforced either way — the only question is whether the CMS resolves it or the driver rejects it.
 *
 * Probed in one batch rather than one query per candidate: a loop of `await`s here would be exactly
 * the N+1 that `npm run query-count` exists to catch, and `path in (...)` seeks the unique index once
 * for the whole set. Twenty-five candidates is far past what any real collision needs, so the common
 * case — nothing taken — costs a single round trip and returns the base slug.
 *
 * A singleton maps every candidate onto the same fixed `/__singleton/{api_id}`, which is harmless:
 * `createItem` has already refused a second one by the time this runs, so the first candidate is free
 * and comes back unchanged.
 */
async function uniquePathSlug(
  db: Kysely<Database>,
  contentType: ContentTypeRow,
  parentPath: string | null,
  desired: string,
  excludeId?: string,
): Promise<string> {
  const base = slugify(desired) || 'item';
  const BATCH = 25;

  for (let start = 1; start < 1000; start += BATCH) {
    const candidates: string[] = [];
    for (let n = start; n < start + BATCH; n++) {
      candidates.push(n === 1 ? base : `${base}-${n}`);
    }
    const paths = candidates.map((slug) => resolveItemPath(contentType, parentPath, slug));

    let query = db.selectFrom('content_items').select('path').where('path', 'in', paths);
    if (excludeId) query = query.where('id', '!=', excludeId);
    const taken = new Set((await query.execute()).map((row) => row.path));

    const free = paths.findIndex((path) => !taken.has(path));
    if (free !== -1) return candidates[free]!;
  }

  throw new ContentItemError(
    `Could not find a free path for "${desired}" after 1000 attempts.`,
    'validation_failed',
  );
}

async function siblingSlugs(
  db: Kysely<Database>,
  contentTypeId: string,
  parentId: string | null,
  excludeId?: string,
): Promise<string[]> {
  let query = db.selectFrom('content_items').select('slug');

  // Collection items are siblings of every other item of their type; page items are siblings only
  // under the same parent.
  query =
    parentId === null
      ? query.where('parent_id', 'is', null).where('content_type_id', '=', contentTypeId)
      : query.where('parent_id', '=', parentId);

  if (excludeId) query = query.where('id', '!=', excludeId);

  return (await query.execute()).map((row) => row.slug);
}

function buildRewriteStatements(
  db: Kysely<Database>,
  rewrites: PathRewrite[],
  root: { rootId: string; parentId: string | null; slug: string; timestamp: string },
): BatchStatement[] {
  return rewrites.map((rewrite) => {
    const patch: Record<string, unknown> = {
      path: rewrite.newPath,
      depth: rewrite.depth,
      updated_at: root.timestamp,
    };

    // Only the moved node itself changes parent and slug; descendants keep theirs and simply
    // inherit the new prefix.
    if (rewrite.id === root.rootId) {
      patch.parent_id = root.parentId;
      patch.slug = root.slug;
    }

    return db.updateTable('content_items').set(patch).where('id', '=', rewrite.id);
  });
}

/**
 * Write a redirect for every path that actually moved.
 *
 * Three things happen per moved path, and each exists because of a specific way redirects rot:
 *
 * 1. **Upsert the new redirect.** A page moved twice would otherwise collide on `from_path`.
 * 2. **Re-point existing redirects that aimed at the old path.** Without this, moving `/a` to `/b`
 *    and later `/b` to `/c` leaves `/a → /b → /c`, a chain the browser has to walk and search
 *    engines penalise. Re-pointing collapses it to `/a → /c`.
 * 3. **Delete any redirect leaving from the new path.** If something previously moved *away* from
 *    `/c` and a live page now occupies `/c`, that stale row is dead weight — the resolver finds
 *    the item first — and would resurrect wrongly if the item moved away again.
 */
function buildRedirectStatements(
  db: Kysely<Database>,
  rewrites: PathRewrite[],
  timestamp: string,
): BatchStatement[] {
  const moved = rewrites.filter((rewrite) => rewrite.oldPath !== rewrite.newPath);
  const statements: BatchStatement[] = [];

  for (const rewrite of moved) {
    statements.push(
      /**
       * Automatic rows only. `source: 'manual'` is documented on the table as "author-created and
       * never GC'd", and now that authors can create them, this is where that promise is kept.
       *
       * Keeping the row is safe as well as promised: the catch-all resolves a content item before
       * it consults the redirect table, so a manual redirect leaving a path a live item now
       * occupies is simply inert — and becomes correct again the moment that item moves away,
       * which is the situation the author wrote it for.
       */
      db
        .deleteFrom('redirects')
        .where('from_path', '=', rewrite.newPath)
        .where('source', '=', 'auto'),

      db
        .insertInto('redirects')
        .values({
          id: newId(),
          from_path: rewrite.oldPath,
          to_path: rewrite.newPath,
          status_code: 301,
          source: 'auto',
          content_item_id: rewrite.id,
          created_at: timestamp,
        })
        .onConflict((oc) =>
          oc.column('from_path').doUpdateSet({ to_path: rewrite.newPath, created_at: timestamp }),
        ),

      db
        .updateTable('redirects')
        .set({ to_path: rewrite.newPath })
        .where('to_path', '=', rewrite.oldPath)
        .where('from_path', '!=', rewrite.newPath),
    );
  }

  return statements;
}
